/**
 * Pure license-info helpers shared by as-components and as-home.
 * No DOM access — consumers render the results themselves.
 */

export interface LicenseData {
  type: string | null;
  /** Number of licensed seats; 0 = no restriction. The API sends this as a numeric string. */
  seats?: number | string;
  startDate: string | null;
  endDate: string | null;
  applications?: string[];
}

/** Tolerant seat-count parser: the API sends `seats` as a numeric string (e.g. "1"). */
function parseSeats(value: number | string | null | undefined): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string' || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export interface LicenseInfo {
  hasLicense: boolean;
  /** Normalized lowercase type, e.g. 'trial' | 'paid' */
  type: string | null;
  isTrial: boolean;
  /** True when the end date has passed */
  isExpired: boolean;
  /** Whole days until endDate (ceil); null when endDate missing/unparseable */
  daysRemaining: number | null;
  /** null = not provided; 0 = unlimited */
  seats: number | null;
  /** Parsed license period bounds; null when missing/unparseable */
  startDate: Date | null;
  endDate: Date | null;
  /** Localized period label, e.g. "20/10/2025 – 30/08/2025" or "until 30/08/2025"; '' when no end date */
  validityLabel: string;
  /** Ready-to-show fallback label, e.g. "Trial – 12 days left" */
  label: string;
  /** Dict key matching the type: license.trial / license.paid / license.none */
  dictKey: string;
  /** Key for `label`, with `labelVars` filling its placeholders. */
  labelDictKey: string;
  labelVars: any[];
  /** True when there is no license or it has expired (render in a warning color) */
  isWarning: boolean;
}

/**
 * Tolerant date parser for license date strings. Accepts ISO strings,
 * server-style "2025-07-15 00:00:00", and pure-digit epoch-seconds strings
 * (dummy data uses "0" / "999999999").
 */
export function parseLicenseDate(value: string | null | undefined): Date | null {
  if (value == null) return null;
  const str = String(value).trim();
  if (!str) return null;

  if (/^\d+$/.test(str)) {
    const date = new Date(parseInt(str, 10) * 1000);
    return isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(str.includes(' ') && !str.includes('T') ? str.replace(' ', 'T') : str);
  return isNaN(date.getTime()) ? null : date;
}

/** Whole days (ceil) until the license end date; null when unavailable. */
export function licenseDaysRemaining(license: LicenseData | null | undefined, now: Date = new Date()): number | null {
  const end = parseLicenseDate(license?.endDate);
  if (!end) return null;
  return Math.ceil((end.getTime() - now.getTime()) / 86_400_000);
}

/** Summarizes a license object into display-ready info. */
export function getLicenseInfo(license: LicenseData | null | undefined, now: Date = new Date()): LicenseInfo {
  const type = license?.type?.toLowerCase() ?? null;
  const hasLicense = !!type;
  const isTrial = type === 'trial';
  const daysRemaining = hasLicense ? licenseDaysRemaining(license, now) : null;
  const seats = parseSeats(license?.seats);
  const startDate = parseLicenseDate(license?.startDate);
  const endDate = parseLicenseDate(license?.endDate);
  const isExpired = hasLicense && endDate != null && endDate.getTime() < now.getTime();
  const validityLabel = !endDate ? '' : startDate
    ? `${startDate.toLocaleDateString()} – ${endDate.toLocaleDateString()}`
    : `until ${endDate.toLocaleDateString()}`;

  // The licence type comes from the server, so it is a value in the sentence
  // rather than part of the key. label stays the English fallback.
  const typeLabel = hasLicense ? type!.charAt(0).toUpperCase() + type!.slice(1) : '';
  let label: string;
  let labelDictKey: string;
  let labelVars: any[];
  if (!hasLicense) {
    label = 'No license';
    labelDictKey = 'license.none';
    labelVars = [];
  } else if (isExpired) {
    label = `${typeLabel} (Expired)`;
    labelDictKey = 'license.expired';
    labelVars = [typeLabel];
  } else if (isTrial && daysRemaining != null) {
    const plural = Math.abs(daysRemaining) === 1 ? '' : 's';
    label = `${typeLabel} – ${daysRemaining} day${plural} left`;
    labelDictKey = Math.abs(daysRemaining) === 1 ? 'license.trialDayLeft' : 'license.trialDaysLeft';
    labelVars = [typeLabel, daysRemaining];
  } else {
    label = typeLabel;
    labelDictKey = `license.type.${type}`;
    labelVars = [];
  }

  return {
    hasLicense,
    type,
    isTrial,
    isExpired,
    daysRemaining,
    seats,
    startDate,
    endDate,
    validityLabel,
    label,
    labelDictKey,
    labelVars,
    dictKey: hasLicense ? `license.${type}` : 'license.none',
    isWarning: !hasLicense || isExpired,
  };
}

/**
 * Ready-to-show seat count label, e.g. "(1 Seat)", "(5 Seats)", "(Unlimited)".
 * '' when seats is null/undefined (not provided by the license).
 */
export function getSeatLabel(seats: number | null | undefined): string {
  if (seats == null) return '';
  if (seats === 0) return '(Unlimited)';
  return `(${seats} Seat${seats === 1 ? '' : 's'})`;
}

/**
 * Seats not yet occupied by a member. null when seats are unlimited (0),
 * or either input is unavailable — callers should treat null as "unknown",
 * not zero.
 */
export function getUnallocatedSeats(seats: number | null | undefined, usedSeats: number | null | undefined): number | null {
  if (seats == null || seats === 0 || usedSeats == null) return null;
  return Math.max(seats - usedSeats, 0);
}
